'use client'
import { logger } from '@/lib/logger';

import React, { useState, useRef, useEffect, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useAriaButton, useKeyboardNavigation, useSkipLinks } from '@/hooks/use-accessibility'
import { usePerformanceMonitor, useFPSMonitor, logBundleSize } from '@/hooks/use-performance-monitor'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import ErrorBoundary from '../components/ErrorBoundary'
import BootLoader from '../components/BootLoader'
import { BootState } from '../components/boot/BootPhaseExecutor'
import SystemStatusPanel from '../components/SystemStatusPanel'
import VisualizerControlsPanel from '../components/VisualizerControlsPanel'
import BootErrorOverlay from '../components/BootErrorOverlay'
import SkipLinks from '../components/SkipLinks'
import SWSNeuralSynthesizer from '../components/audio/SWSNeuralSynthesizer'
import CosmicWizardSpells from '../components/audio/CosmicWizardSpells'
import SWSUnifiedVisualizer from '../components/visualization/SWSUnifiedVisualizer'
import SWSWebGPUAccelerator from '../components/audio/SWSWebGPUAccelerator'
import SWSAIStyleTransfer from '../components/audio/SWSAIStyleTransfer'
import SWSCollaborativeStudio from '../components/audio/SWSCollaborativeStudio'
import { getAudioContext, ensureAudioContextRunning, resetAudioContext, shouldResetAudioContext } from '../lib/audio/audioContext'
import {
  DashboardLayout,
  Sidebar,
  MainContent,
  Panel,
  Grid,
  Header
} from '../components/ui/layout'
import SettingsPanel from '../components/SettingsPanel'

export default function Home() {
  const audioContextRef = useRef<AudioContext | null>(null)
  const [isBootComplete, setIsBootComplete] = useState(false)
  const [bootError, setBootError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [initializationError, setInitializationError] = useState<string | null>(null)
  const [currentAudioData, setCurrentAudioData] = useState<Float32Array | undefined>(undefined)
  const [neuralActivity, setNeuralActivity] = useState<number[]>([])
  const [frequencyData, setFrequencyData] = useState<Float32Array | undefined>(undefined)
  const [visualizerMode, setVisualizerMode] = useState<'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial'>('waveform')
  const [activeTool, setActiveTool] = useState<'neural' | 'webgpu' | 'transfer' | 'collaborative' | 'cosmic'>('neural')
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  // Accessibility hooks
  const { skipToContent, skipToNavigation } = useSkipLinks()

  // Performance monitoring
  const performanceMetrics = usePerformanceMonitor({
    componentName: 'Main App',
    logThreshold: 16,
    trackMemory: true
  })
  const fps = useFPSMonitor()

  // Boot loader handlers
  const handleBootComplete = useCallback((bootState: BootState) => {
    logger.log('Boot sequence completed successfully!')

    // Store the audio context reference from boot
    if (bootState.audioContext) {
      audioContextRef.current = bootState.audioContext
      logger.log('Audio context reference obtained from boot')
    }

    setIsBootComplete(true)
    setBootError(null)

    // Check if auto-resume was successful
    if (bootState.autoResumeSuccessful) {
      logger.log('🎉 System is fully operational - auto-resume successful!')
      setIsInitialized(true)
      setInitializationError(null)
    } else {
      logger.log('⚠️ Auto-resume failed - audio will start on user interaction')
      // If auto-resume failed, set up interaction handlers but don't set error
      setIsInitialized(false)
      setInitializationError(null)
    }
  }, [])

  const handleBootError = useCallback((error: string) => {
    logger.error('Boot sequence failed:', error)
    setBootError(error)
    setIsBootComplete(true) // Still show the app but with error state
  }, [])

  // Cleanup function to reset audio context reference on component unmount
  useEffect(() => {
    return () => {
      logger.log('Cleaning up audio context reference on component unmount')
      audioContextRef.current = null
    }
  }, [])

  // Cleanup effect for page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      logger.log('Page unloading, resetting audio context')
      resetAudioContext()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Performance indicator
  const getPerformanceColor = (fps: number) => {
    if (fps >= 50) return 'text-green-400'
    if (fps >= 30) return 'text-yellow-400'
    return 'text-red-400'
  }

  // Generate sample neural activity data
  const generateNeuralActivity = useCallback(() => {
    const activity = Array.from({ length: 256 }, () =>
      Math.sin(Date.now() * 0.001 + Math.random() * 10) * 0.5 + 0.5
    )
    setNeuralActivity(activity)
  }, [])

  // Generate sample frequency data
  const generateFrequencyData = useCallback(() => {
    if (!currentAudioData || currentAudioData.length === 0) return

    const fftSize = 2048
    const frequencies = new Float32Array(fftSize / 2)

    for (let i = 0; i < frequencies.length; i++) {
      const frequency = (i / frequencies.length) * 22050 // Up to Nyquist frequency
      const magnitude = Math.abs(Math.sin(frequency * 0.001 + Date.now() * 0.001)) * 0.5
      frequencies[i] = magnitude
    }

    setFrequencyData(frequencies)
  }, [currentAudioData])

  // Handle user interaction to resume audio context
  const handleUserInteraction = useCallback(async () => {
    // If already initialized, no need to do anything
    if (isInitialized) {
      return
    }

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        logger.log('Attempting to resume AudioContext after user interaction')
        const result = await audioContextRef.current.resume()
        logger.log('AudioContext resume result:', result)

        // Update state to show audio is now active
        const currentState = audioContextRef.current?.state as AudioContextState | undefined
        if (currentState === 'running') {
          setIsInitialized(true)
          setInitializationError(null)
          logger.log('🎉 Audio engine is now ACTIVE!')
        } else if (currentState === 'suspended') {
          logger.log('AudioContext still suspended after resume attempt')
          // Don't set error - just wait for next interaction
        }
      } catch (error) {
        logger.error('Failed to resume AudioContext:', error)
        // Don't set error - just wait for next interaction
      }
    }
  }, [isInitialized])

  // Add global interaction handlers for audio context resume
  useEffect(() => {
    const events = ['click', 'keydown', 'touchstart', 'scroll', 'mousemove']
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction)
      })
    }
  }, [handleUserInteraction])

  // Periodic status check for audio context state (only after boot is complete and not yet initialized)
  useEffect(() => {
    if (!isBootComplete || isInitialized) return

    const statusInterval = setInterval(() => {
      if (audioContextRef.current) {
        const state = audioContextRef.current.state
        if (state === 'running' && !isInitialized) {
          // Context was resumed by user interaction
          setIsInitialized(true)
          setInitializationError(null)
          logger.log('Audio context started via user interaction')
        }
      }
    }, 100) // Check every 100ms

    return () => clearInterval(statusInterval)
  }, [isBootComplete, isInitialized])

  // Update visualization data periodically (only after boot is complete)
  useEffect(() => {
    if (!isBootComplete) return

    const interval = setInterval(() => {
      generateNeuralActivity()
      generateFrequencyData()
    }, 50) // ~20fps

    return () => clearInterval(interval)
  }, [generateNeuralActivity, generateFrequencyData, isBootComplete])

  // Keyboard shortcuts handlers
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }, [isSidebarCollapsed])

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  const handleSkipToNavigation = useCallback(() => {
    const firstFocusable = document.querySelector('[role="radiogroup"] button[tabindex="0"]') as HTMLElement
    firstFocusable?.focus()
  }, [])

  const handleToggleAudioPlayback = useCallback(async () => {
    if (!audioContextRef.current) return

    if (audioContextRef.current.state === 'running') {
      await audioContextRef.current.suspend()
    } else if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume()
    }
  }, [])

  const handleVisualizerModeChange = useCallback((mode: typeof visualizerMode) => {
    setVisualizerMode(mode)
  }, [])

  // Use keyboard shortcuts hook
  useKeyboardShortcuts({
    onToggleSidebar: handleToggleSidebar,
    onOpenSettings: handleOpenSettings,
    onSkipToNavigation: handleSkipToNavigation,
    onToggleAudioPlayback: handleToggleAudioPlayback,
    onVisualizerModeChange: handleVisualizerModeChange,
    isInitialized,
    audioContextState: audioContextRef.current?.state
  })

  // Show boot loader if boot is not complete
  if (!isBootComplete) {
    return (
      <BootLoader
        onComplete={handleBootComplete}
        onError={handleBootError}
      />
    )
  }

  return (
    <>
      {/* Skip Links for Accessibility */}
      <SkipLinks
        onSkipToContent={skipToContent}
        onSkipToNavigation={skipToNavigation}
      />

      {/* Show boot error overlay if there was an error */}
      <BootErrorOverlay
        error={bootError}
        onReload={() => window.location.reload()}
      />

      <DashboardLayout
        collapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        data-testid="main-app"
        sidebar={
          <Sidebar collapsed={isSidebarCollapsed}>
            {/* Supreme Status Panel */}
            <SystemStatusPanel
              isInitialized={isInitialized}
              audioContextState={audioContextRef.current?.state}
              initializationError={initializationError}
              fps={fps}
            />

            {/* Supreme Visualizer Controls */}
            <VisualizerControlsPanel
              visualizerMode={visualizerMode}
              onModeChange={setVisualizerMode}
            />

            {/* Revolutionary Tool Selector */}
            <Panel title="Revolutionary Tools" icon="🚀" collapsible={true} defaultOpen={true}>
              <div
                className="space-y-1"
                role="radiogroup"
                aria-label="Tool selection"
                aria-describedby="tools-description"
              >
                <div id="tools-description" className="sr-only">
                  Select an AI tool for audio processing. Use arrow keys to navigate and Enter to select.
                </div>
                {[
                  { tool: 'neural', icon: '🧠', name: 'Neural Synth', desc: 'AI Synthesis Engine', shortcut: 'Q' },
                  { tool: 'webgpu', icon: '⚡', name: 'WebGPU ML', desc: 'GPU Accelerated AI', shortcut: 'W' },
                  { tool: 'transfer', icon: '🎨', name: 'AI Transfer', desc: 'Style Transformation', shortcut: 'E' },
                  { tool: 'collaborative', icon: '🌐', name: 'Collaborative', desc: 'Multi-user Studio', shortcut: 'R' },
                  { tool: 'cosmic', icon: '🧙‍♂️', name: 'Cosmic Spells', desc: 'Wizard Audio Generators', shortcut: 'T' }
                ].map(({ tool, icon, name, desc, shortcut }, index) => {
                  const isSelected = activeTool === tool

                  return (
                    <button
                      key={tool}
                      onClick={() => setActiveTool(tool as typeof activeTool)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' && index < 3) {
                          e.preventDefault()
                          const nextButton = e.currentTarget.nextElementSibling as HTMLElement
                          nextButton?.focus()
                        } else if (e.key === 'ArrowUp' && index > 0) {
                          e.preventDefault()
                          const prevButton = e.currentTarget.previousElementSibling as HTMLElement
                          prevButton?.focus()
                        } else if (e.key === 'q' || e.key === 'Q') {
                          e.preventDefault()
                          setActiveTool('neural')
                        } else if (e.key === 'w' || e.key === 'W') {
                          e.preventDefault()
                          setActiveTool('webgpu')
                        } else if (e.key === 'e' || e.key === 'E') {
                          e.preventDefault()
                          setActiveTool('transfer')
                        } else if (e.key === 'r' || e.key === 'R') {
                          e.preventDefault()
                          setActiveTool('collaborative')
                        } else if (e.key === 't' || e.key === 'T') {
                          e.preventDefault()
                          setActiveTool('cosmic')
                        }
                      }}
                      role="radio"
                      aria-checked={isSelected ? 'true' : 'false'}
                      aria-label={`Activate ${name}: ${desc}. Shortcut: ${shortcut}`}
                      aria-current={isSelected ? 'page' : undefined}
                      tabIndex={isSelected ? 0 : -1}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 text-left group focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                        isSelected
                          ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 shadow-lg shadow-purple-500/20'
                          : 'hover:bg-slate-700/50 text-slate-300 hover:border-slate-600/50 border border-transparent'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center relative ${
                        isSelected
                          ? 'bg-purple-500/20'
                          : 'bg-slate-700/50 group-hover:bg-slate-600/50'
                      }`}>
                        <span className="text-sm" aria-hidden="true">{icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{name}</div>
                        <div className="text-xs text-slate-500 leading-tight">{desc}</div>
                      </div>
                      {isSelected && (
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" aria-hidden="true"></div>
                      )}
                    </button>
                  )
                })}
              </div>
            </Panel>

            {/* Supreme Quick Actions */}
            <Panel title="Command Center" icon="⚡" collapsible={true} defaultOpen={false}>
              <div className="space-y-3">
                <button
                  aria-label="Generate a new audio sample"
                  className="w-full flex items-center space-x-3 p-3 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 hover:from-blue-500/30 hover:to-cyan-500/30 border border-blue-500/30 hover:border-blue-400/50 rounded-lg text-sm font-medium text-blue-300 hover:text-blue-200 transition-all duration-200 group supreme-btn focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-offset-2 focus:ring-offset-slate-800"
                >
                  <div className="w-6 h-6 bg-blue-500/20 group-hover:bg-blue-400/30 rounded flex items-center justify-center">
                    <span className="text-xs" aria-hidden="true">🎵</span>
                  </div>
                  <span>Generate Sample</span>
                </button>

                <button
                  aria-label="Export current audio to file"
                  className="w-full flex items-center space-x-3 p-3 bg-gradient-to-r from-green-600/20 to-emerald-600/20 hover:from-green-500/30 hover:to-emerald-500/30 border border-green-500/30 hover:border-green-400/50 rounded-lg text-sm font-medium text-green-300 hover:text-green-200 transition-all duration-200 group supreme-btn focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:ring-offset-2 focus:ring-offset-slate-800"
                >
                  <div className="w-6 h-6 bg-green-500/20 group-hover:bg-green-400/30 rounded flex items-center justify-center">
                    <span className="text-xs" aria-hidden="true">💾</span>
                  </div>
                  <span>Export Audio</span>
                </button>

                <button
                  aria-label="Load audio preset from library"
                  className="w-full flex items-center space-x-3 p-3 bg-gradient-to-r from-purple-600/20 to-violet-600/20 hover:from-purple-500/30 hover:to-violet-500/30 border border-purple-500/30 hover:border-purple-400/50 rounded-lg text-sm font-medium text-purple-300 hover:text-purple-200 transition-all duration-200 group supreme-btn focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:ring-offset-2 focus:ring-offset-slate-800"
                >
                  <div className="w-6 h-6 bg-purple-500/20 group-hover:bg-purple-400/30 rounded flex items-center justify-center">
                    <span className="text-xs" aria-hidden="true">📁</span>
                  </div>
                  <span>Load Preset</span>
                </button>

                <div className="pt-2 border-t border-slate-600/50">
                  <button
                    onClick={() => setIsSettingsOpen(true)}
                    aria-label="Open application settings panel"
                    className="w-full flex items-center space-x-3 p-2 hover:bg-slate-700/50 rounded text-xs text-slate-400 hover:text-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:ring-offset-2 focus:ring-offset-slate-800"
                  >
                    <span aria-hidden="true">⚙️</span>
                    <span>Settings</span>
                  </button>
                </div>
              </div>
            </Panel>
          </Sidebar>
        }

        main={
          <MainContent>
            {/* Supreme Header */}
            <div className="mb-8">
              <div className="text-center space-y-4">
                <Header
                  title="Sound Wizard Studio"
                  subtitle="Professional Audio Synthesis & Analysis Engine"
                />

                <div className="flex items-center justify-center space-x-6 text-xs text-slate-400">
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                    <span>Generation</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                    <span>Processing</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                    <span>Enhancement</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                    <span>Analysis</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
                    <span>Output</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Revolutionary Tool Workspace */}
            <div className="space-y-6">
              {/* Active Revolutionary Tool */}
              <Panel
                title={
                  activeTool === 'neural' ? 'Neural Synthesis' :
                  activeTool === 'webgpu' ? '⚡ WebGPU ML Accelerator' :
                  activeTool === 'transfer' ? '🎨 AI Style Transfer' :
                  activeTool === 'collaborative' ? '🌐 Collaborative Studio' :
                  '🧙‍♂️ Cosmic Wizard Spells'
                }
                icon={
                  activeTool === 'neural' ? '⚡' :
                  activeTool === 'webgpu' ? '⚡' :
                  activeTool === 'transfer' ? '🎨' :
                  activeTool === 'collaborative' ? '🌐' :
                  '🧙‍♂️'
                }
                collapsible={false}
                defaultOpen={true}
              >
                <div className="mb-4">
                </div>

                {activeTool === 'neural' && (
                  <ErrorBoundary name="Neural Synthesizer" recovery={true}>
                    <SWSNeuralSynthesizer />
                  </ErrorBoundary>
                )}
                {activeTool === 'webgpu' && (
                  <ErrorBoundary name="WebGPU Accelerator" recovery={true}>
                    <SWSWebGPUAccelerator />
                  </ErrorBoundary>
                )}
                {activeTool === 'transfer' && (
                  <ErrorBoundary name="AI Style Transfer" recovery={true}>
                    <SWSAIStyleTransfer />
                  </ErrorBoundary>
                )}
                {activeTool === 'collaborative' && (
                  <ErrorBoundary name="Collaborative Studio" recovery={true}>
                    <SWSCollaborativeStudio />
                  </ErrorBoundary>
                )}
                {activeTool === 'cosmic' && (
                  <ErrorBoundary name="Cosmic Wizard Spells" recovery={true}>
                    <CosmicWizardSpells
                      onAudioData={setCurrentAudioData}
                      onFrequencyData={setFrequencyData}
                      onNeuralActivity={setNeuralActivity}
                    />
                  </ErrorBoundary>
                )}
              </Panel>

            </div>

          {/* 5. Output Layer - Visualizer */}
          <Panel title="Audio Visualizer" icon="📈" collapsible={true} defaultOpen={true}>
              <div className="mb-4">
                <div className="flex items-center space-x-2 text-xs text-cyan-400 font-medium text-supreme">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full status-pulse shadow-lg shadow-cyan-500/30"></div>
                  <span>VISUALIZATION OUTPUT</span>
                </div>
              </div>
              <ErrorBoundary name="Audio Visualizer" recovery={true}>
                <SWSUnifiedVisualizer
                  audioData={currentAudioData}
                  neuralActivity={neuralActivity}
                  frequencyData={frequencyData}
                  width={800}
                  height={400}
                  mode={visualizerMode}
                />
              </ErrorBoundary>
            </Panel>
          </MainContent>
        }
      />

      {/* Settings Panel */}
      <ErrorBoundary name="Settings Panel" recovery={true}>
        <SettingsPanel
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </ErrorBoundary>
    </>
  )
}